import React from 'react';
import {
   Card,
   CardContent,
   Typography,
   Avatar,
   Box,
   Chip,
} from '@mui/material';
import {
   Event as EventIcon,
   Lock as LockIcon,
   Public as PublicIcon,
   CalendarToday as CalendarTodayIcon,
   LocationOn as LocationOnIcon,
   VideoCall as VideoCallIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const TEAL = '#1D9E75';
const GOLD = '#C9A84C';
const CARD_BG = '#111815';
const STAT_LABEL = '#546E7A';

export interface EventCreated {
   id: string;
   card_type: 'event_created';
   userid: string;
   eventId: string;
   eventTitle: string;
   eventDescription?: string;
   eventCoverUrl?: string;
   eventStartDate: string;
   eventEndDate?: string;
   eventLocation?: string;
   eventType?: string;
   eventIsPublic: boolean;
   eventIsVirtual: boolean;
   creatorId: string;
   creatorName: string;
   creatorPhoto?: string;
   creatorEmail: string;
   createdAt: any;
   seen: boolean;
}

interface EventCreatedCardProps {
   eventCreated: EventCreated;
   onViewEvent: (eventId: string) => void;
   onMarkAsSeen?: (cardId: string) => void;
}

const EventCreatedCard: React.FC<EventCreatedCardProps> = ({
   eventCreated,
   onViewEvent,
   onMarkAsSeen,
}) => {
   const handleClick = () => {
      if (onMarkAsSeen) {
         onMarkAsSeen(eventCreated.id);
      }
      onViewEvent(eventCreated.eventId);
   };

   const getTimeAgo = () => {
      if (!eventCreated.createdAt) return '';
      try {
         const date =
            eventCreated.createdAt.toDate?.() || new Date(eventCreated.createdAt.seconds * 1000);
         return formatDistanceToNow(date, { addSuffix: true });
      } catch {
         return '';
      }
   };

   const formatEventDate = (date: any) => {
      try {
         let dateObj;
         if (date?.toDate) {
            dateObj = date.toDate();
         } else if (date?.seconds) {
            dateObj = new Date(date.seconds * 1000);
         } else if (typeof date === 'string') {
            dateObj = new Date(date);
         } else {
            dateObj = date;
         }

         return dateObj.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
         });
      } catch {
         return '';
      }
   };

   const getLocationString = (location: any) => {
      if (!location) return '';
      if (typeof location === 'string') return location;
      if (typeof location === 'object') {
         return location.name || location.address || '';
      }
      return '';
   };

   const coverUrl = eventCreated.eventCoverUrl;
   const isPublic = eventCreated.eventIsPublic;
   const isVirtual = eventCreated.eventIsVirtual;
   const startDate = eventCreated.eventStartDate;
   const location = eventCreated.eventLocation;

   const privacyChip = isPublic
      ? {
           bg: 'rgba(201,168,76,0.12)',
           color: GOLD,
           border: '1px solid rgba(201,168,76,0.3)',
           icon: <PublicIcon sx={{ fontSize: 14 }} />,
           label: 'Public',
        }
      : {
           bg: 'rgba(29,158,117,0.12)',
           color: TEAL,
           border: '1px solid rgba(29,158,117,0.3)',
           icon: <LockIcon sx={{ fontSize: 14 }} />,
           label: 'Private',
        };

   return (
      <Card
         sx={{
            mb: 1.5,
            background: CARD_BG,
            border: '1px solid #1F2E2A',
            borderTop: `4px solid ${TEAL}`,
            borderRadius: '12px',
            overflow: 'visible',
            transition: 'all 0.2s',
            cursor: 'pointer',
            '&:hover': {
               boxShadow: '0 4px 16px rgba(29,158,117,0.12)',
               transform: 'translateY(-1px)',
            },
         }}
         onClick={handleClick}
      >
         <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
               {coverUrl ? (
                  <Box
                     component="img"
                     src={coverUrl}
                     alt={eventCreated.eventTitle}
                     sx={{
                        width: 72,
                        height: 72,
                        objectFit: 'cover',
                        borderRadius: '12px',
                        flexShrink: 0,
                     }}
                  />
               ) : (
                  <Avatar
                     sx={{
                        width: 72,
                        height: 72,
                        bgcolor: TEAL,
                        borderRadius: '12px',
                        flexShrink: 0,
                     }}
                  >
                     <EventIcon sx={{ fontSize: 36, color: 'white' }} />
                  </Avatar>
               )}

               <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                     <Typography
                        variant="caption"
                        sx={{
                           fontWeight: 700,
                           color: TEAL,
                           fontSize: '11px',
                           letterSpacing: 2,
                           textTransform: 'uppercase',
                        }}
                     >
                        New Event Created!
                     </Typography>
                     <Chip
                        icon={privacyChip.icon}
                        label={privacyChip.label}
                        size="small"
                        sx={{
                           height: 20,
                           fontSize: '0.7rem',
                           fontWeight: 600,
                           backgroundColor: privacyChip.bg,
                           color: privacyChip.color,
                           border: privacyChip.border,
                           '& .MuiChip-icon': {
                              color: privacyChip.color,
                           },
                        }}
                     />
                     {isVirtual && (
                        <Chip
                           icon={<VideoCallIcon sx={{ fontSize: 14 }} />}
                           label="Virtual"
                           size="small"
                           sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: 'rgba(201,168,76,0.12)',
                              color: GOLD,
                              border: '1px solid rgba(201,168,76,0.3)',
                              '& .MuiChip-icon': {
                                 color: GOLD,
                              },
                           }}
                        />
                     )}
                  </Box>

                  <Typography
                     sx={{
                        fontWeight: 700,
                        fontSize: '16px',
                        color: '#FFFFFF',
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                     }}
                  >
                     {eventCreated.eventTitle}
                  </Typography>

                  {eventCreated.eventDescription && (
                     <Typography
                        variant="body2"
                        sx={{
                           color: '#B0BEC5',
                           mb: 1,
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           display: '-webkit-box',
                           WebkitLineClamp: 2,
                           WebkitBoxOrient: 'vertical',
                        }}
                     >
                        {eventCreated.eventDescription}
                     </Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: TEAL }} />
                        <Typography variant="caption" sx={{ color: STAT_LABEL }}>
                           {formatEventDate(startDate)}
                        </Typography>
                     </Box>

                     {getLocationString(location) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <LocationOnIcon sx={{ fontSize: 16, color: TEAL }} />
                           <Typography
                              variant="caption"
                              sx={{
                                 color: STAT_LABEL,
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap',
                                 maxWidth: 150,
                              }}
                           >
                              {getLocationString(location)}
                           </Typography>
                        </Box>
                     )}

                     <Typography variant="caption" sx={{ color: STAT_LABEL, ml: 'auto' }}>
                        {getTimeAgo()}
                     </Typography>
                  </Box>
               </Box>
            </Box>
         </CardContent>
      </Card>
   );
};

export default EventCreatedCard;
